import { useEffect, useRef } from 'react';

/**
 * Hides all elements outside of the provided primary element ref from screen readers
 * by setting aria-hidden, and focuses the element on open.
 *
 * @param {object} [params]
 * @param {boolean} [params.isVisible] - Whether the modal is currently open
 * @param {React.RefObject} [params.primaryFocusElementRef] - A ref to the primary focus element
 */
export function useAriaModal({ isVisible, primaryFocusElementRef }) {
  const previouslyHidden = useRef([]);

  useEffect(() => {
    const node = primaryFocusElementRef.current;
    if (!isVisible || !node) {
      return undefined;
    }

    const siblings = Array.from(document.body.children).filter(
      (el) => el !== node && !node.contains(el),
    );

    siblings.forEach((el) => {
      if (el.getAttribute('aria-hidden') !== 'true') {
        el.setAttribute('aria-hidden', 'true');
        previouslyHidden.current.push(el);
      }
    });
    node.focus();

    return () => {
      previouslyHidden.current.forEach((el) => {
        el.removeAttribute('aria-hidden');
      });
      previouslyHidden.current = [];
    };
  }, [isVisible, primaryFocusElementRef]);
}
